import React from "react";
import styled from "styled-components";
import {
  TILES,
  SPRITE_CELL_WIDTH,
  SPRITE_CELL_HEIGHT,
  SPRITE_COLS,
  SPRITE_ROWS,
  SPRITE_SHEET_URL,
} from "./tiles";

// Display default for a rendered Tile: the source sheet's native 71×96
// cell, 1:1 — never fractionally upscale pixel art.
export const DEFAULT_TILE_WIDTH = 71;
export const DEFAULT_TILE_HEIGHT =
  DEFAULT_TILE_WIDTH * (SPRITE_CELL_HEIGHT / SPRITE_CELL_WIDTH);

const RANK_WORDS = {
  a: "ace",
  j: "jack",
  q: "queen",
  k: "king",
};

// "heart-10" → "10 of hearts"; non-card sprites ("back-3", "recycle")
// fall back to the raw name. Only spoken for button tiles — div tiles
// are decoration and stay silent.
function tileLabel(name) {
  const m = /^(spade|heart|club|diamond)-(a|[2-9]|10|j|q|k)$/.exec(name);
  if (!m) return name.replace(/-/g, " ");
  return `${RANK_WORDS[m[2]] ?? m[2]} of ${m[1]}s`;
}

function Tile({
  name,
  width = DEFAULT_TILE_WIDTH,
  height,
  as = "div",
  className,
  selected = false,
  ...delegated
}) {
  const [row, col] = TILES[name];
  // Buttons get a spoken label; decorative div tiles are hidden from
  // the accessibility tree instead.
  const extraProps =
    as === "button"
      ? { type: "button", "aria-label": tileLabel(name) }
      : { "aria-hidden": "true" };

  // Default to the sprite's native size if neither dimension is given.
  // If exactly one is given, leave the other undefined so CSS aspect-ratio
  // derives it from the source ratio. If both are given, the caller has
  // explicitly opted out of aspect locking.
  const resolvedWidth = width ?? (height == null ? SPRITE_CELL_WIDTH : undefined);
  const resolvedHeight = height ?? (width == null ? SPRITE_CELL_HEIGHT : undefined);

  return (
    <Root
      as={as}
      $row={row}
      $col={col}
      $width={resolvedWidth}
      $height={resolvedHeight}
      data-selected={selected ? "true" : undefined}
      className={className}
      {...extraProps}
      {...delegated}
    />
  );
}

const Root = styled.div`
  display: inline-block;
  /* Tiles are fractional-height inline-blocks; on the default baseline
     alignment, div↔button swaps can shift a container's height by a
     sub-pixel. Pin to top so baseline math never participates in layout. */
  vertical-align: top;
  ${({ $width }) => $width != null && `width: ${$width}px;`}
  ${({ $height }) => $height != null && `height: ${$height}px;`}
  aspect-ratio: ${SPRITE_CELL_WIDTH} / ${SPRITE_CELL_HEIGHT};

  background-image: url("${SPRITE_SHEET_URL}");
  background-color: transparent;
  background-repeat: no-repeat;
  background-size: ${SPRITE_COLS * 100}% ${SPRITE_ROWS * 100}%;
  background-position: ${({ $col, $row }) =>
    `${($col / (SPRITE_COLS - 1)) * 100}% ${($row / (SPRITE_ROWS - 1)) * 100}%`};

  border: none;
  box-shadow: none;
  outline: none;
  min-height: 0;
  min-width: 0;
  padding: 0;
  color: inherit;
  text-shadow: none;
  font: inherit;
  cursor: default;

  /* Kill 98.css's pressed-button chrome while a tile is being clicked —
     ALL tiles, selected or not; the selection glow is the ::after wash
     below, which box-shadow can't touch. */
  &&:not(:disabled):active {
    box-shadow: none;
    text-shadow: none;
  }

  &&:focus {
    outline: none;
  }
  /* Mouse clicks stay outline-free; tabbing shows the Win95 dotted
     marquee so keyboard players can see where they are. */
  &&:focus-visible {
    outline: 1px dotted #fff;
    outline-offset: -3px;
  }

  /* Hover styles scoped to the button variant only (div-rendered Tiles
     unaffected). Specificity (0,3,1) is enough to beat 98.css's own
     button hover rules in @media (not (hover)) contexts. */
  &:is(button):not(:disabled):hover {
    cursor: pointer;
    /* Tiles stay chrome-free on hover too — without this, the global
       touch-device hover-neutralizer (global-styles.css) would win and
       raise a bevel on every tapped card. */
    box-shadow: none;
  }

  /* Cards are drag sources; without this, touch devices turn the drag
     into a page scroll before it can start. */
  touch-action: none;

  /* Selection: a dark navy wash, the way Explorer tinted a selected
     icon (not sol.exe's full color-inversion, which reads as jarring).
     Driven by the data-selected attribute rather than a
     styled-components dynamic prop so the rule is statically present
     in the stylesheet. */
  position: relative;

  &&[data-selected="true"]::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 5px;
    background: rgba(0, 0, 123, 0.32);
  }
`;

export default Tile;
